import { useEffect, useMemo } from "react";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import { athlete1RMV2Enabled, athleteProfilingV2Enabled } from "config/features";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";

/**
 * Hotspots on the Profiling and 1RM chart type buttons announcing velocity-based 1RM.
 * Each one is dismissed once its visualization is opened.
 */
function useAthleteProfileChartVelocityHotspots() {
  const dataVisualizationType = useDataVisualizationType();
  const profiling = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteProfilingVelocityHotspot);
  const oneRepMax = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteOneRepMaxVelocityHotspot);

  const profilingOpen = athleteProfilingV2Enabled && profiling.open;
  const oneRepMaxOpen = athlete1RMV2Enabled && oneRepMax.open;
  const dismissProfiling = profiling.dismiss;
  const dismissOneRepMax = oneRepMax.dismiss;

  useEffect(() => {
    if (profilingOpen && dataVisualizationType === DataVisualizationType.PROFILING_CHART) dismissProfiling();
  }, [dataVisualizationType, dismissProfiling, profilingOpen]);

  useEffect(() => {
    if (oneRepMaxOpen && dataVisualizationType === DataVisualizationType.ONE_REP_MAX_CHART) dismissOneRepMax();
  }, [dataVisualizationType, dismissOneRepMax, oneRepMaxOpen]);

  return useMemo<Partial<Record<DataVisualizationType, boolean>>>(
    () => ({
      [DataVisualizationType.PROFILING_CHART]: profilingOpen,
      [DataVisualizationType.ONE_REP_MAX_CHART]: oneRepMaxOpen,
    }),
    [oneRepMaxOpen, profilingOpen]
  );
}

export default useAthleteProfileChartVelocityHotspots;
